import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Smile } from "lucide-react";
import api from "../../api";
import MoodChart from "./MoodChart";

// Mood check-ins (backend clinical/monitoring/checkins_routes.py): the patient's own
// 1-10 ratings from My Care, a trend, their notes, and how often the app
// reminds them.
export default function MoodCheckinsCard({ patientId }) {
  const [data, setData] = useState(null);
  const load = () => api.getPatientCheckins(patientId).then((r) => setData(r.data)).catch(() => setData({ frequency: "off", checkins: [] }));
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line

  const setFreq = async (frequency) => {
    try {
      await api.setCheckinFrequency(patientId, frequency);
      setData((d) => ({ ...d, frequency }));
      toast.success(frequency === "off" ? "Reminders off - the patient can still check in" : `The app will remind them ${frequency}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save that.");
    }
  };

  if (!data) return null;
  const rows = data.checkins || [];
  const notes = [...rows].reverse().filter((r) => r.note).slice(0, 6);
  const latest = rows[rows.length - 1];
  return (
    <div className="rounded-xl bg-white border border-secondary-100 p-4 md:p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><Smile className="w-5 h-5 text-primary-600" /> Mood check-ins</h3>
        {latest && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${latest.mood <= 3 ? "bg-rose-100 text-rose-700" : "bg-secondary-100 text-secondary-700"}`}>Latest {latest.mood}/10</span>}
        <div className="ml-auto flex items-center gap-2 text-xs text-secondary-700">
          <span>Remind the patient</span>
          <select value={data.frequency} onChange={(e) => setFreq(e.target.value)} aria-label="Check-in reminders" className="rounded-md border border-secondary-300 bg-white px-2 py-1 text-xs">
            <option value="off">never</option><option value="daily">daily</option><option value="weekly">weekly</option>
          </select>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-secondary-600">No check-ins yet. The patient can rate their mood any day from My Care; turn on reminders to nudge them.</p>
      ) : (
        <>
          <MoodChart points={rows} />
          <p className="text-xs text-secondary-600">{rows.length} check-in{rows.length === 1 ? "" : "s"} in the last 90 days. A rating of 2 or below, or a drop of 3 or more, alerts the care team.</p>
          {notes.length > 0 && (
            <ul className="space-y-1.5">
              {notes.map((r) => (
                <li key={r.id} className="text-sm text-secondary-800">
                  <span className="mr-2 text-xs font-semibold text-secondary-500">{new Date(`${r.day}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {r.mood}/10</span>
                  {r.note}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
